import React, { ReactNode, useEffect, useState } from 'react';
import process from 'process';
import {
  Box,
  BoxProps,
  Button,
  Chip,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import {
  BoardDetailHeaderSx,
  BoardDetailMenu,
  BoardDetailSubText,
} from '@/components/Board/BoardDetail/styles';
import { PAGE_URL } from '@/constant/url';
import { copyClipBoardText } from '@/utils';
import CommentIcon from '@/public/icons/board/comments.svg';
import ReadMoreIcon from '@/public/icons/board/kebab.svg';
import CloseIcon from '@/public/icons/close_black.svg';
import LinkIcon from '@/public/icons/link2.svg';
import PrintIcon from '@/public/icons/print_20.svg';
import { ActionType } from '@/constant/button';
import { BoardDetailData } from '@/types/board';
import { BoardDetailHeaderId } from '@/components/Board/BoardDetailModal';
import classNames from 'classnames';
import BookmarkToggleButton from '@/components/BookmarkToggleButton';
import ProfileCardPopper from '@/components/@IntranetLibrary/ProfileCard/ProfileCardPopper';
import usePopper from '@/hooks/usePopper';
import { useSnackbar } from '@/hooks/global-tools';
import usePlatform from '@/components/Record/hooks/usePlatform';
import { useIsAppEmbedded } from '@/utils/appEmbedded';
import useDetectScroll from '@smakss/react-scroll-direction';
import useBoardDetailCommentCount from '@/components/Board/BoardDetail/Feedback/BoardDetailComment/useBoardDetailCommentCount';

type BoardDetailHeaderProps = {
  isMobile: boolean;
  id?: string;
  scrollRef?: React.RefObject<HTMLDivElement | null>;
  disabled?: boolean;
  onClickComment: () => void;
  onAction: (action: ActionType, params?: any) => void;
  onPrint: () => void;
  data: BoardDetailData;
  isBookmark: boolean;
  extraActionNode?: ReactNode;
  onClose?: () => void;
} & BoxProps;

const BoardDetailHeader = (_props: BoardDetailHeaderProps) => {
  const {
    isMobile,
    id = BoardDetailHeaderId,
    scrollRef,
    disabled,
    onClickComment,
    onAction,
    onPrint,
    data,
    isBookmark,
    extraActionNode,
    onClose,
    sx,
    ...props
  } = _props;
  const iconSize = isMobile ? 28 : 24;
  const iconStyle = { width: `${iconSize}px`, height: `${iconSize}px` };
  const [scrollTarget, setScrollTarget] = useState<HTMLDivElement>();
  const { scrollPosition } = useDetectScroll({
    target: scrollTarget,
  });
  const [isCompact, setIsCompact] = useState(false);
  const { totalCount: commentCount } = useBoardDetailCommentCount(
    data?.commentGroupId ?? '',
  );

  useEffect(() => {
    if (scrollRef?.current) setScrollTarget(scrollRef.current);
  }, [scrollRef]);

  useEffect(() => {
    const newIsCompact = scrollPosition.top > 0;
    if (newIsCompact !== isCompact) {
      setIsCompact(newIsCompact);
    }
  }, [scrollPosition.top, isCompact]);

  const { subject, id: documentId, createdBy, config } = data;

  const { isMobilePath } = usePlatform();
  const isAppEmbedded = useIsAppEmbedded();
  const { isReserved, isDeleteEnable, displayStartedAt } = config;
  const isSandboxMobileHeader = isMobile && config.isSandbox;
  const now = new Date();
  const startDate = new Date(displayStartedAt);

  const { anchorEl, closePopper } = usePopper();

  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(
    null,
  );
  const isMenuOpen = Boolean(menuAnchorEl);
  const toast = useSnackbar();

  const closeMenu = () => setMenuAnchorEl(null);
  const clickMenu = async (action: ActionType) => {
    if (disabled) return;

    switch (action) {
      case ActionType.LINK_COPY: {
        const resultCopy = await copyClipBoardText(
          `${process.env.NEXT_PUBLIC_ORIGIN_URL}${PAGE_URL.BOARD_DETAIL(
            documentId,
          )}`,
        );
        if (resultCopy) {
          toast.showSuccess({ message: '링크가 복사되었습니다.' });
        }
        break;
      }
      case ActionType.PRINT:
        onPrint();
        break;
      case ActionType.DELETE:
      case ActionType.EDIT:
        onAction(action);
        break;
    }
    // 메뉴 닫기
    setMenuAnchorEl(null);
  };

  const showReserveChip = isReserved && startDate > now;

  const menu = (
    <BoardDetailMenu
      id={'menu'}
      anchorEl={menuAnchorEl}
      open={disabled ? false : isMenuOpen}
      onClose={closeMenu}
      MenuListProps={{ 'aria-labelledby': 'menu-button' }}
    >
      <MenuItem
        data-menu-type={ActionType.LINK_COPY}
        onClick={() => clickMenu(ActionType.LINK_COPY)}
      >
        링크 복사
        <LinkIcon width={'20px'} height={'20px'} />
      </MenuItem>
      <MenuItem
        data-menu-type={ActionType.PRINT}
        onClick={() => clickMenu(ActionType.PRINT)}
        // 인쇄는 모바일 경로와, 네이티브 앱이 헤더 없이 띄운 화면(embedded)에서 숨긴다
        sx={{ display: isMobilePath || isAppEmbedded ? 'none' : undefined }}
      >
        인쇄
        <PrintIcon />
      </MenuItem>
      <MenuItem
        data-menu-type={ActionType.EDIT}
        onClick={() => clickMenu(ActionType.EDIT)}
        sx={{ display: isDeleteEnable ? undefined : 'none' }}
      >
        수정
      </MenuItem>
      <MenuItem
        data-menu-type={ActionType.DELETE}
        onClick={() => clickMenu(ActionType.DELETE)}
        sx={{ display: isDeleteEnable ? undefined : 'none' }}
      >
        삭제
      </MenuItem>
    </BoardDetailMenu>
  );

  if (isSandboxMobileHeader) {
    return (
      <Box
        id={id}
        component={'header'}
        className={classNames({ isCompact, isMobile, isSandboxMobileHeader })}
        sx={{
          ...BoardDetailHeaderSx,
          padding: '14px 16px',
          backgroundColor: 'var(--dk-bg, #FFFFFF)',
          borderBottom: '1px solid var(--dk-line, #E7E9EC)',
          '.board-title': {
            backgroundColor: 'var(--dk-bg, #FFFFFF)',
          },
          ...sx,
        }}
        {...props}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr minmax(0, auto) 1fr',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <Stack direction={'row'} alignItems={'center'} spacing={'12px'}>
            <Button
              id={'menu-button'}
              aria-controls={isMenuOpen ? 'basic-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={isMenuOpen ? 'true' : undefined}
              aria-label="더보기"
              disabled={disabled}
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                setMenuAnchorEl(event.currentTarget);
              }}
              sx={{ padding: 0, width: 28, height: 28, minWidth: 28 }}
            >
              <ReadMoreIcon {...iconStyle} />
            </Button>
            <BookmarkToggleButton
              isChecked={isBookmark}
              onChange={() => onAction(ActionType.BOOKMARK)}
              theme="black"
              sx={{
                ...iconStyle,
                svg: iconStyle,
              }}
            />
          </Stack>

          <Typography
            component={'h1'}
            className={'board-title'}
            sx={{
              maxWidth: 'calc(100vw - 168px)',
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              textAlign: 'center',
              fontSize: '16px',
              fontWeight: 400,
              lineHeight: '24px',
              color: 'var(--dk-text, #000000)',
            }}
          >
            {subject}
          </Typography>

          <Stack
            direction={'row'}
            alignItems={'center'}
            justifyContent={'flex-end'}
            spacing={'12px'}
          >
            {extraActionNode}
            {onClose && (
              <Button
                disabled={disabled}
                aria-label="닫기"
                onClick={onClose}
                sx={{ padding: 0, width: 28, height: 28, minWidth: 28 }}
              >
                <CloseIcon width={28} height={28} />
              </Button>
            )}
          </Stack>
        </Box>

        {menu}
        <ProfileCardPopper
          anchorEl={anchorEl}
          passportId={createdBy.passportId}
          onClose={closePopper}
        />
      </Box>
    );
  }

  return (
    <Box
      id={id}
      component={'header'}
      className={classNames({ isCompact, isMobile })}
      sx={{ ...BoardDetailHeaderSx, ...sx }}
      {...props}
    >
      <Stack direction={'row'} gap={'12px'}>
        {isMobile ? (
          <>
            {showReserveChip && (
              <Chip
                key={`category_isReserved`}
                variant={'outlined'}
                color={'primary'}
                label={'예약게시글'}
              />
            )}
          </>
        ) : (
          <>
            {showReserveChip && (
              <Chip
                key={`category_isReserved`}
                variant={'outlined'}
                color={'primary'}
                label={'예약게시글'}
              />
            )}
            <Typography component={'h1'} className={'board-title ellipsis'}>
              {subject}
            </Typography>
          </>
        )}
        <Stack
          direction={'row'}
          alignItems={'center'}
          spacing={'12px'}
          className={'util-button-wrap'}
          sx={{
            marginLeft: 'auto',
          }}
        >
          {/* 댓글 수 */}
          <Button
            disabled={disabled}
            onClick={onClickComment}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '2px',
              '&:hover': { backgroundColor: 'var(--dk-bg-muted, #F3F5F6)' },

              padding: 0,
            }}
          >
            <CommentIcon {...iconStyle} />
            <BoardDetailSubText
              fontSize={isMobile ? '16px' : '14px'}
              marginLeft={'2px'}
            >
              {commentCount.toLocaleString()}
            </BoardDetailSubText>
          </Button>

          {/* 즐겨찾기 */}
          <BookmarkToggleButton
            isChecked={isBookmark}
            onChange={() => onAction(ActionType.BOOKMARK)}
            theme="black"
            sx={{
              ...iconStyle,
              svg: iconStyle,
            }}
          />

          {extraActionNode}

          {/* 메뉴 버튼 */}
          <Button
            id={'menu-button'}
            aria-controls={isMenuOpen ? 'basic-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={isMenuOpen ? 'true' : undefined}
            disabled={disabled}
            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
              setMenuAnchorEl(event.currentTarget);
            }}
            sx={{ padding: 0, ...iconStyle }}
          >
            <ReadMoreIcon {...iconStyle} />
          </Button>
          {/* 메뉴 */}
          {menu}

          {/* 닫기 — 앱 웹뷰의 일반 글(BoardDetail 이 onClose 를 내려준 경우). 샌드박스 헤더의 X 와 같은 모양 */}
          {onClose && (
            <Button
              disabled={disabled}
              aria-label="닫기"
              onClick={onClose}
              sx={{
                padding: 0,
                width: iconSize,
                height: iconSize,
                minWidth: iconSize,
              }}
            >
              <CloseIcon width={iconSize} height={iconSize} />
            </Button>
          )}
        </Stack>
      </Stack>

      {isMobile && !isCompact && (
        <Box
          sx={{
            overflow: 'hidden',
            padding: '12px 2px 0 2px',
            marginBottom: '2px',
          }}
        >
          <Typography component={'h1'} className={'board-title ellipsis-line2'}>
            {subject}
          </Typography>
        </Box>
      )}
      <ProfileCardPopper
        anchorEl={anchorEl}
        passportId={createdBy.passportId}
        onClose={closePopper}
      />
    </Box>
  );
};

export default BoardDetailHeader;
